/* ============================================
   CrossChannelContext — Shows related conversations
   from other channels when a contact is linked.
   Displayed as a collapsible card in ChatWindow.
   ============================================ */

import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import {
  IconGlobe, IconWhatsApp, IconMail, IconMessageCircle,
  IconChevronDown, IconChevronRight,
} from '@/components/icons/Icons'
import type { CanalChat } from '@/lib/types'

interface CrossChannelContextProps {
  contactoId: number
  currentConversationId: number
  currentCanal: CanalChat
}

interface RelatedConversation {
  id: number
  canal: CanalChat
  estado: string
  created_at: string
  updated_at: string
  mensaje_count: number
  last_message: string | null
}

function channelIcon(canal: CanalChat, size = 14) {
  switch (canal) {
    case 'whatsapp': return <IconWhatsApp size={size} />
    case 'email': return <IconMail size={size} />
    case 'web_chat': return <IconGlobe size={size} />
    default: return <IconMessageCircle size={size} />
  }
}

function channelLabel(canal: CanalChat): string {
  switch (canal) {
    case 'whatsapp': return 'WhatsApp'
    case 'email': return 'Email'
    case 'web_chat': return 'Web Chat'
    default: return canal
  }
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = now - then
  const minutes = Math.floor(diff / 60000)
  if (minutes < 60) return `hace ${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `hace ${hours}h`
  const days = Math.floor(hours / 24)
  return `hace ${days}d`
}

export default function CrossChannelContext({
  contactoId,
  currentConversationId,
}: CrossChannelContextProps) {
  const [related, setRelated] = useState<RelatedConversation[]>([])
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      setLoading(true)
      // Get other conversations for this contact
      const { data: convs } = await supabase
        .from('conversaciones')
        .select('id, canal, estado, created_at, updated_at')
        .eq('contacto_id', contactoId)
        .neq('id', currentConversationId)
        .order('updated_at', { ascending: false })
        .limit(5)

      if (!convs || convs.length === 0) {
        setLoading(false)
        return
      }

      // Get message counts and last message for each
      const enriched: RelatedConversation[] = await Promise.all(
        convs.map(async (conv: { id: number; canal: CanalChat; estado: string; created_at: string; updated_at: string }) => {
          const { count } = await supabase
            .from('mensajes')
            .select('*', { count: 'exact', head: true })
            .eq('conversacion_id', conv.id)

          const { data: lastMsg } = await supabase
            .from('mensajes')
            .select('contenido')
            .eq('conversacion_id', conv.id)
            .order('fecha_mensaje', { ascending: false })
            .limit(1) as { data: { contenido: string }[] | null }

          return {
            ...conv,
            mensaje_count: count || 0,
            last_message: lastMsg?.[0]?.contenido?.substring(0, 80) || null,
          }
        })
      )

      setRelated(enriched)
      setLoading(false)
    }

    if (contactoId) load()
  }, [contactoId, currentConversationId, supabase])

  if (loading || related.length === 0) return null

  return (
    <div style={{
      margin: '0 12px 8px',
      background: 'var(--bg-tertiary)',
      borderRadius: 8,
      border: '1px solid var(--border-color)',
      fontSize: 12,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          padding: '8px 12px',
          background: 'none',
          border: 'none',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        {expanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
        💬 {related.length} conversación{related.length > 1 ? 'es' : ''} en otros canales
      </button>

      {expanded && (
        <div style={{ padding: '0 12px 8px' }}>
          {related.map((conv) => (
            <div
              key={conv.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 0',
                borderTop: '1px solid var(--border-color)',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                {channelIcon(conv.canal)}
                <strong>{channelLabel(conv.canal)}</strong>
              </span>
              <span style={{ color: 'var(--text-muted)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {conv.last_message || '(sin mensajes)'}
              </span>
              <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: 11 }}>
                {conv.mensaje_count} msgs · {timeAgo(conv.updated_at)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
